import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Options, SpinnerSize } from '@predictiveindex/tapestry';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { UserProfileRequest } from '../../core-data-model/UserProfileRequest';
import { AvatarService, CompanySettingsApi } from '../../core-services';
import { UserProfileApi } from '../../core-services/UserProfile/user-profile-api.connnect';
import {
    FeatureFlags,
    FeatureFlagsService,
    InitDataService
} from '../../system-services';

@Component({
    selector: 'my-info',
    templateUrl: './my-info.component.html',
    styleUrls: ['./my-info.component.less'],
    standalone: false
})
export class MyInfoComponent implements OnInit, OnChanges, OnDestroy {
    @Input() editable = false;

    @Output() userNameChanged = new EventEmitter<string>();

    loaded$ = new BehaviorSubject(false);
    spinnerSize: SpinnerSize = SpinnerSize.Medium;

    enableCustomAvatars = false;
    enableNameEditing = false;
    enablePronounsEditing = false;
    enablePreferredNameEditing = false;

    pronounOptions: Options[] = [
        {
            value: '---',
            displayValue: '---',
            isDisabled: false
        },
        {
            value: 'He/Him',
            displayValue: 'He/Him',
            isDisabled: false
        },
        {
            value: 'She/Her',
            displayValue: 'She/Her',
            isDisabled: false
        },
        {
            value: 'They/Them',
            displayValue: 'They/Them',
            isDisabled: false
        },
        {
            value: 'Other pronouns',
            displayValue: 'Other pronouns',
            isDisabled: false
        }
    ];

    currentlySelectedAvatar = '';
    selectedPronounOption: string = this.pronounOptions[0].value;
    myInfoForm: FormGroup;

    private destroyed$ = new Subject<void>();

    constructor(
        private avatarService: AvatarService,
        private fb: FormBuilder,
        private initData: InitDataService,
        private featureFlags: FeatureFlagsService,
        private companySettingsApi: CompanySettingsApi,
        private userProfileRepository: UserProfileApi
    ) {}

    ngOnInit() {
        this.myInfoForm = this.fb.group({
            firstName: [
                {
                    value: this.initData.profile?.firstName,
                    disabled: !this.editable
                }
            ],
            lastName: [
                {
                    value: this.initData.profile?.lastName,
                    disabled: !this.editable
                },
                [Validators.required]
            ],
            preferredName: [
                {
                    value: this.initData.profile?.firstName,
                    disabled: !this.editable
                }
            ]
        });

        this.updatePersonalizationOptions();
        this.getUserProfileInfo();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.editable.currentValue !== changes.editable.previousValue) {
            if (changes.editable.currentValue) {
                this.myInfoForm.enable();
            } else {
                this.myInfoForm.disable();
            }
        }
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    avatarUrlChanged(avatarUrl: string) {
        this.currentlySelectedAvatar = avatarUrl;
    }

    getUserProfileInfo() {
        this.userProfileRepository
            .getUserProfileInfo()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((profileInfo) => {
                this.myInfoForm.controls.firstName.setValue(
                    profileInfo.firstName
                );
                this.myInfoForm.controls.lastName.setValue(
                    profileInfo.lastName
                );
                this.myInfoForm.controls.preferredName.setValue(
                    profileInfo.preferredName
                );
                this.selectedPronounOption = profileInfo.pronouns;
            });
    }

    submitChanges() {
        const userProfileUpdate: UserProfileRequest = {
            userGuid: this.initData.user.userGuid,
            userEmail: this.initData.user.email,
            firstName: this.myInfoForm.controls.firstName.value,
            lastName: this.myInfoForm.controls.lastName.value,
            preferredName: this.myInfoForm.controls.preferredName.value,
            pronouns: this.selectedPronounOption
        };
        this.userProfileRepository
            .updateUserProfileInfo(userProfileUpdate)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((response) => {
                if (response) {
                    this.avatarService.selectPreferredAvatar(
                        this.currentlySelectedAvatar
                    );
                    const fullName =
                        `${userProfileUpdate.firstName} ${userProfileUpdate.lastName}`.trim();
                    this.userNameChanged.emit(fullName);
                    this.editable = false;
                    this.myInfoForm.disable();
                }
            });
    }

    updatePersonalizationOptions() {
        this.companySettingsApi
            .fetchPersonalizationSettings()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((settings) => {
                this.enableCustomAvatars =
                    settings.areAvatarsEnabled &&
                    this.featureFlags.getByFlag(
                        FeatureFlags.enableCustomAvatars
                    );
                this.enableNameEditing =
                    settings.isNameEditingEnabled &&
                    this.featureFlags.getByFlag(
                        FeatureFlags.enableMyProfileNameEditing
                    );
                this.enablePronounsEditing =
                    settings.arePronounsEnabled &&
                    this.featureFlags.getByFlag(
                        FeatureFlags.enableMyProfilePronounsEditing
                    );
                this.enablePreferredNameEditing =
                    settings.isPreferredNameEnabled &&
                    this.featureFlags.getByFlag(
                        FeatureFlags.enableMyProfilePreferredNameEditing
                    );

                this.loaded$.next(true);
            });
    }

    updateSelectedPronoun(selectedPronoun) {
        this.selectedPronounOption = selectedPronoun;
    }
}
